import type React from 'react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { type LoadResponse, loadService } from '../services/loadService'
import { tagService } from '../services/tagService'
import type { Tag } from '../types/tag'
import CardRow from './card/CardRow'

interface CumulativeLoadInfoProps {
  tag?: Tag
}

const CumulativeLoadInfo: React.FC<CumulativeLoadInfoProps> = ({ tag }) => {
  const { tagId } = useParams()
  const [cumulativeData, setCumulativeData] = useState({
    totalLoads: '0',
    totalWeight: '0',
    totalTime: '0h 0m',
    totalTolls: '$0.00',
    timeBreakdown: {
      loadTime: '0h 0m',
      yardTime: '0h 0m',
      homeTime: '0h 0m',
    },
  })

  useEffect(() => {
    const calculateCumulativeData = async () => {
      if (!tagId) return

      try {
        const loads = await loadService.getLoadsByTag(tagId)

        // Calculate total loads
        const totalLoads = loads.length.toString()

        // Calculate total weight
        const totalWeight = loads.reduce((sum, load) => {
          const weight = Number.parseFloat(load.weight || '0')
          return sum + weight
        }, 0)

        // Get total time from tagService
        const totalHours = await tagService.calculateTotalTime(tagId, tag)
        const wholeHours = Math.floor(totalHours)
        const minutes = Math.round((totalHours - wholeHours) * 60)
        const totalTime = `${wholeHours}h ${minutes}m`

        // Calculate total tolls from loads and additional expenses
        const loadTolls = loads.reduce((sum, load) => {
          const toll = Number.parseFloat(load.tolls || '0')
          return sum + toll
        }, 0)
        const additionalTolls = Number.parseFloat(
          tag?.additional_expenses?.tolls || '0',
        )
        const totalTolls = loadTolls + additionalTolls

        // Calculate time breakdown
        const yardTimeMinutes = Number.parseInt(
          tag?.additional_expenses?.yardTime || '0',
        )
        const homeTimeMinutes = Number.parseInt(
          tag?.additional_expenses?.homeTime || '0',
        )
        const yardTimeMs = yardTimeMinutes * 60 * 1000
        const homeTimeMs = homeTimeMinutes * 60 * 1000

        setCumulativeData({
          totalLoads,
          totalWeight: `${totalWeight.toLocaleString()}`,
          totalTime,
          totalTolls: `$${totalTolls.toFixed(2)}`,
          timeBreakdown: {
            loadTime: formatTime(
              totalHours * 60 * 60 * 1000 - yardTimeMs - homeTimeMs,
            ),
            yardTime: formatTime(yardTimeMs),
            homeTime: formatTime(homeTimeMs),
          },
        })
      } catch (error) {
        console.error('Failed to fetch cumulative load data:', error)
      }
    }

    calculateCumulativeData()
  }, [tagId, tag])

  const formatTime = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60))
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))
    return `${hours}h ${minutes}m`
  }

  const getTimeBreakdownTooltip = () => {
    const { loadTime, yardTime, homeTime } = cumulativeData.timeBreakdown
    return `Load Time: ${loadTime}\nYard Time: ${yardTime}\nHome Time: ${homeTime}`
  }

  return (
    <>
      <CardRow
        label="TOTAL # LOADS"
        value={cumulativeData.totalLoads}
        vertical
      />
      <CardRow
        label="TOTAL WEIGHT"
        value={cumulativeData.totalWeight}
        vertical
      />
      <CardRow
        label="TIME"
        value={cumulativeData.totalTime}
        tooltip={getTimeBreakdownTooltip()}
        vertical
      />
      <CardRow label="TOLLS" value={cumulativeData.totalTolls} vertical />
    </>
  )
}

export default CumulativeLoadInfo
